import type { BoxShopItemFormValues } from "@/modules/boxShop/form/boxShopFormik"
import {
  type BoxShopItemDto,
  type BoxShopItemInputDto,
  BoxShopItemCategory,
} from "@/modules/boxShop/gateway/boxShopApiDto"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { PriceObjectEntity } from "@/modules/common/priceObject/entities/priceObjectEntity"
import { convertImageFromDto } from "@/modules/dtos/imageDto"
import { type HUImage } from "@modules/types/imageTypes"

export type BoxShopItemState = Omit<BoxShopItemEntity, "setStock">

export class BoxShopItemEntity {
  id: string
  title: string
  brand: string | null
  description: string
  category: BoxShopItemCategory
  price: PriceObjectEntity
  taxAmount: number | null
  stock: number
  image: HUImage | null

  private constructor(product: BoxShopItemState) {
    this.id = product.id
    this.title = product.title
    this.brand = product.brand
    this.description = product.description
    this.category = product.category
    this.price = product.price
    this.taxAmount = product.taxAmount
    this.stock = product.stock
    this.image = product.image
  }

  static fromDto(dto: BoxShopItemDto): BoxShopItemEntity {
    return new BoxShopItemEntity({
      id: dto.id,
      title: dto.title,
      brand: dto.brand,
      description: dto.description,
      category: dto.category,
      price: PriceObjectEntity.fromDto(dto.price),
      taxAmount: dto.taxAmount,
      stock: dto.stock,
      image: dto.image ? convertImageFromDto(dto.image) : null,
    })
  }

  static fromEntityToFormik(product?: BoxShopItemEntity): BoxShopItemFormValues {
    if (!product) {
      return {
        title: "",
        brand: null,
        description: "",
        category: null,
        price: 0,
        stock: null,
        image: null,
      }
    }
    return {
      title: product.title,
      brand: product.brand,
      description: product.description,
      category: product.category,
      price: product.price.value,
      stock: product.stock,
      image: product.image
        ? {
            url: product.image.url,
            key: product.image.key ?? null,
            type: MediaType.S3_IMAGE,
            filename: null,
            isUploading: false,
            file: null,
          }
        : null,
    }
  }

  static fromFormikToInputDto(formik: BoxShopItemFormValues): BoxShopItemInputDto {
    return {
      title: formik.title,
      brand: formik.brand ?? null,
      description: formik.description.trim(),
      category: formik.category ?? BoxShopItemCategory.SNACK,
      price: formik.price,
      stock: formik.stock ?? 0,
      imageKey: formik.image?.key ?? null,
    }
  }

  setStock(stock: number): BoxShopItemEntity {
    return new BoxShopItemEntity({
      id: this.id,
      title: this.title,
      brand: this.brand,
      description: this.description,
      category: this.category,
      price: this.price,
      taxAmount: this.taxAmount,
      stock,
      image: this.image,
    })
  }
}
